import { useContent } from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  useGetPersonalDataQuery,
  useSaveTaxDataMutation,
} from 'store/slices/personalData-api';
import { taxInformationContent } from '../taxInformation.content';
import {
  Button,
  Heading,
  SelectButtonLikeInput,
  TextInputAnimatedForm,
  Typography,
} from '@altify/components';

import { useGetCountriesQuery } from 'store/slices/references-api';
import { useState } from 'react';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CountrySelectionModal from '../components/CountrySelectionModal/CountrySelectionModal';
import TaxInformationSuccessModal from '../components/TaxInformationSuccessModal/TaxInformationSuccessModal';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';

const MAX_ADDRESS_LENGTH = 255;
const MAX_POSTCODE_LENGTH = 50;
export const Route = createFileRoute(
  '/_authenticated/tax-information/edit-tax-information',
)({
  component: EditTaxInformation,
});

function EditTaxInformation() {
  const router = useRouter();
  const localizedContent = useContent(taxInformationContent);
  const {
    data: personalData,
    isLoading: personalDataLoading,
    isFetching: personalDataFetching,
  } = useGetPersonalDataQuery();
  const { data: countries, isLoading: countriesLoading } =
    useGetCountriesQuery();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [saveTaxData, saveTaxDataResult] = useSaveTaxDataMutation();
  const schema = z.object({
    addressLine1: z
      .string()
      .min(1, localizedContent.addressLine1Required)
      .max(MAX_ADDRESS_LENGTH, localizedContent.addressLine1MaxLength),
    city: z
      .string()
      .min(1, localizedContent.cityRequired)
      .max(MAX_ADDRESS_LENGTH, localizedContent.cityMaxLength),
    postCode: z
      .string()
      .min(1, localizedContent.postCodeRequired)
      .max(MAX_POSTCODE_LENGTH, localizedContent.postCodeMaxLength),
    addressLine2: z.string().min(0).max(MAX_ADDRESS_LENGTH).optional(),
    countryOfResidence: z.string().min(2).max(2),
    countryOfTaxResidence: z.string().min(2).max(2),
    taxIdentificationNumber: z
      .string()
      .min(1, localizedContent.taxIdentificationNumberIsRequired)
      .max(50, localizedContent.taxIdentificationNumberMaxLength),
  });

  type FormSchema = z.infer<typeof schema>;

  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    values: {
      addressLine1: personalData?.addressLine1 ?? '',
      addressLine2: personalData?.addressLine2 ?? '',
      city: personalData?.residencyCity ?? '',
      postCode: personalData?.zipCode ?? '',
      countryOfResidence: personalData?.residencyCountryCode ?? '',
      countryOfTaxResidence: personalData?.countryOfTaxResidence ?? '',
      taxIdentificationNumber: personalData?.taxIdentificationNumber ?? '',
    },
  });

  const onSubmit = handleSubmit(async data => {
    const result = await saveTaxData(data);
    if (!result.error) {
      setShowSuccessModal(true);
    }
  });

  const [showCountryOfResidenceSearch, setShowCountryOfResidenceSearch] =
    useState(false);
  const [showCountryOfTaxResidenceSearch, setShowCountryOfTaxResidenceSearch] =
    useState(false);

  const showLoader =
    personalDataLoading || personalDataFetching || countriesLoading;

  if (showLoader) {
    return <CustomLoader page />;
  }

  return (
    <div className="w-full items-center">
      <div className="flex flex-col w-full items-center flex-1">
        <div className="flex flex-col items-center mb-10">
          <Heading level={4} className="mb-3 mt-5 text-center">
            {localizedContent.reviewDetailsTitle}
          </Heading>
        </div>
        <div className="flex flex-col items-center gap-6 w-full">
          <TextInputAnimatedForm
            name={'addressLine1'}
            label={localizedContent.addressLine1}
            placeholder={localizedContent.addressLine1}
            control={control}
            fullWidth={true}
          />
          <TextInputAnimatedForm
            name={'addressLine2'}
            label={localizedContent.addressLine2}
            placeholder={localizedContent.addressLine2}
            control={control}
            fullWidth={true}
          />
          <TextInputAnimatedForm
            name={'city'}
            label={localizedContent.city}
            placeholder={localizedContent.city}
            control={control}
            fullWidth={true}
          />
          <TextInputAnimatedForm
            name={'postCode'}
            label={localizedContent.postCode}
            placeholder={localizedContent.postCode}
            control={control}
            fullWidth={true}
          />
          <Controller
            name="countryOfResidence"
            control={control}
            render={({ field }) => (
              <SelectButtonLikeInput
                label={localizedContent.countryOfResidence}
                placeholder={localizedContent.countryOfResidence}
                value={countries?.find(c => c.code === field.value)?.name}
                onPress={() => {
                  setShowCountryOfResidenceSearch(true);
                  field.onChange(field.value);
                }}
              />
            )}
          />
          <Controller
            name="countryOfTaxResidence"
            control={control}
            render={({ field }) => (
              <SelectButtonLikeInput
                label={localizedContent.countryOfTaxResidence}
                placeholder={localizedContent.countryOfTaxResidence}
                value={countries?.find(c => c.code === field.value)?.name}
                onPress={() => {
                  setShowCountryOfTaxResidenceSearch(true);
                  field.onChange(field.value);
                }}
              />
            )}
          />
          <TextInputAnimatedForm
            name={'taxIdentificationNumber'}
            label={localizedContent.taxIdentificationNumber}
            placeholder={localizedContent.taxIdentificationNumber}
            control={control}
            fullWidth={true}
          />
        </div>
      </div>
      <div className="w-full pb-2 pt-10 items-center flex flex-col gap-4">
        {saveTaxDataResult.error && (
          <Typography className="my-5 text-center text-error">
            {localizedContent.unexpectedErrorOccurred}
          </Typography>
        )}
        <Button
          size="large"
          color="primary"
          onPress={onSubmit}
          disabled={!isValid}
          loading={saveTaxDataResult.isLoading}
          text={localizedContent.updateDetails}
          className="w-full"
        />
      </div>
      <CountrySelectionModal
        isOpen={showCountryOfResidenceSearch}
        handleClose={() => setShowCountryOfResidenceSearch(false)}
        onSelect={value => setValue('countryOfResidence', value)}
      />
      <CountrySelectionModal
        isOpen={showCountryOfTaxResidenceSearch}
        handleClose={() => setShowCountryOfTaxResidenceSearch(false)}
        onSelect={value => setValue('countryOfTaxResidence', value)}
      />
      <TaxInformationSuccessModal
        isOpen={showSuccessModal}
        isUpdate={true}
        onClose={() => {
          setShowSuccessModal(false);
          router.navigate({ to: '/tax-information/review-tax-information' });
        }}
      />
    </div>
  );
}

export default EditTaxInformation;
